import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NIVEL_TURMA, type NivelTurma } from '@arena/shared/dominio';
import {
  MOTIVOS_DE_SAIDA_PEDIDA,
  ROTULO_MOTIVO_DE_SAIDA,
  ROTULO_NIVEL_TURMA,
  zAlteracaoDeTurma,
  zMotivoDaTurma,
  zSaidaDaTurma,
  type AlteracaoDeTurma,
  type AlunoMatriculado,
  type DetalheDaTurma,
} from '@arena/shared/schemas';
import { adicionarDias, MINUTOS_DO_DIA } from '@arena/shared/tempo';
import { alunosApi } from '../api/alunos.js';
import { agendaApi } from '../api/agenda.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { turmasApi } from '../api/turmas.js';
import { rotuloDeDuracao } from '../agenda/formatos.js';
import { janelaDaGrade } from '../agenda/layout.js';
import { errosPorCampo, opcoesDeInicio } from '../agenda/ModalDeReserva.jsx';
import { useAtrasado } from '../agenda/useAtrasado.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import {
  Avatar,
  Botao,
  Campo,
  Escolhas,
  Formulario,
  Mini,
  Nota,
  Status,
  Vazio,
} from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { corDaLotacao, dataCompleta, gradeResumida, haDias } from './formatos.js';

/**
 * A turma aberta — o botão "Alunos" dos cards de `#v-turmas`.
 *
 * O protótipo abre `#m-evento` pelo card da turma; aqui a turma tem a
 * própria tela, porque é nela que mora o que o `#m-evento` de uma AULA não
 * tem: matricular, tirar da turma, transferir, a lista de espera, mudar a
 * grade e o ciclo de vida. A aula continua com a aba "Alunos da turma" na
 * agenda.
 *
 * O que muda a GRADE — dias, horário, quadra, professor — vale de amanhã
 * em diante: a aula de hoje fica como está, e as aulas remarcadas uma a
 * uma ficam onde a pessoa as pôs.
 */

type Modo =
  | { tipo: 'ver' }
  | { tipo: 'editar' }
  | { tipo: 'suspender' }
  | { tipo: 'encerrar' }
  | { tipo: 'sair'; matricula: AlunoMatriculado }
  | { tipo: 'transferir'; matricula: AlunoMatriculado };

export function ModalDaTurma({
  id,
  onFechar,
  onChamada,
}: {
  id: string;
  onFechar: () => void;
  onChamada: (marcacaoId: string) => void;
}) {
  const consulta = useQuery({
    queryKey: ['turmas', 'detalhe', id],
    queryFn: () => turmasApi.detalhe(id),
    retry: false,
  });

  if (consulta.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Carregando a turma…" largura="larga">
        <CorpoDeModal>
          <EsqueletoDeCard linhas={5} />
        </CorpoDeModal>
      </Modal>
    );
  }
  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Modal aberto onFechar={onFechar} titulo="Turma" largura="estreita">
        <CorpoDeModal>
          <Erro
            tipo={
              erro instanceof ErroDeRede
                ? 'rede'
                : erro instanceof ErroDeApi && erro.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(erro)}
          />
        </CorpoDeModal>
      </Modal>
    );
  }
  return <TurmaAberta turma={consulta.data} onFechar={onFechar} onChamada={onChamada} />;
}

function TurmaAberta({
  turma,
  onFechar,
  onChamada,
}: {
  turma: DetalheDaTurma;
  onFechar: () => void;
  onChamada: (marcacaoId: string) => void;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();
  const [modo, setModo] = useState<Modo>({ tipo: 'ver' });
  const [erro, setErro] = useState<string | null>(null);

  const podeEditar = pode('turmas', 'editar') && turma.status !== 'Encerrada';
  const invalidar = () => {
    void cliente.invalidateQueries({ queryKey: ['turmas'] });
    void cliente.invalidateQueries({ queryKey: ['agenda'] });
    void cliente.invalidateQueries({ queryKey: ['alunos'] });
  };

  const trocar = (novo: Modo) => {
    setErro(null);
    setModo(novo);
  };

  async function executar<T>(acao: () => Promise<T>, mensagem: (resultado: T) => string) {
    setErro(null);
    try {
      const resultado = await acao();
      invalidar();
      avisar(mensagem(resultado));
      setModo({ tipo: 'ver' });
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  const titulo = turma.titulo;
  const descricao = [
    gradeResumida(turma.grade),
    turma.grade?.quadra.nome,
    `Prof. ${turma.professor.nome}`,
  ]
    .filter(Boolean)
    .join(' · ');

  const rodape = (
    <>
      <span className="left">Criada em {dataCompleta(turma.criadaEm.slice(0, 10))}</span>
      <Botao
        variante="fantasma"
        onClick={modo.tipo === 'ver' ? onFechar : () => trocar({ tipo: 'ver' })}
      >
        {modo.tipo === 'ver' ? 'Fechar' : 'Voltar'}
      </Botao>
      {modo.tipo === 'ver' && podeEditar && (
        <Botao variante="contorno" onClick={() => trocar({ tipo: 'editar' })}>
          Editar turma
        </Botao>
      )}
      {modo.tipo === 'ver' && turma.aulaParaChamada && pode('turmas', 'ler') && (
        <Botao onClick={() => onChamada(turma.aulaParaChamada!.marcacaoId)}>
          {pode('turmas', 'registrar_chamada') ? 'Fazer chamada' : 'Ver chamada'}
        </Botao>
      )}
    </>
  );

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="larga"
      marca={
        <span
          className="chip"
          style={{
            background: corDaLotacao(turma.matriculados, turma.capacidade),
            color: 'var(--on-ac)',
          }}
          aria-hidden="true"
        >
          <Icone nome="ball" />
        </span>
      }
      titulo={titulo}
      descricao={descricao}
      rodape={rodape}
    >
      <CorpoDeModal>
        {turma.status !== 'Ativa' && (
          <div className={`aviso${turma.status === 'Encerrada' ? ' conflito' : ''}`}>
            <Icone nome="info" />
            <div>
              <b>Turma {turma.status === 'Suspensa' ? 'suspensa' : 'encerrada'}</b>
              {turma.motivoStatus ? ` · ${turma.motivoStatus}` : ''}.{' '}
              {turma.status === 'Suspensa'
                ? 'As aulas saíram da grade e as matrículas continuam.'
                : 'A série acabou e as matrículas foram encerradas.'}
            </div>
          </div>
        )}

        {modo.tipo === 'ver' && (
          <>
            <Mini
              itens={[
                {
                  valor: `${turma.matriculados} de ${turma.capacidade}`,
                  rotulo: 'alunos matriculados',
                },
                {
                  valor: turma.frequencia === null ? '—' : `${turma.frequencia}%`,
                  rotulo: 'frequência em 8 semanas',
                },
                {
                  valor: turma.esperando,
                  rotulo: turma.esperando === 1 ? 'na lista de espera' : 'na lista de espera',
                },
              ]}
            />

            <ListaDeAlunos
              turma={turma}
              podeEditar={podeEditar}
              onSair={(matricula) => trocar({ tipo: 'sair', matricula })}
              onTransferir={(matricula) => trocar({ tipo: 'transferir', matricula })}
            />

            {podeEditar && <Matricular turma={turma} onFeito={invalidar} />}

            <ListaDeEspera turma={turma} podeEditar={podeEditar} onFeito={invalidar} />

            {pode('turmas', 'editar') && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {turma.status === 'Ativa' && (
                  <Botao variante="contorno" pequeno onClick={() => trocar({ tipo: 'suspender' })}>
                    Suspender turma
                  </Botao>
                )}
                {turma.status === 'Suspensa' && (
                  <Botao
                    variante="contorno"
                    pequeno
                    onClick={() =>
                      void executar(
                        () => turmasApi.reativar(turma.id),
                        (r) => `Turma reativada. ${r.aulas} aulas voltaram para a grade.`,
                      )
                    }
                  >
                    Reativar turma
                  </Botao>
                )}
                {turma.status !== 'Encerrada' && (
                  <Botao
                    variante="contorno"
                    pequeno
                    style={{ borderColor: 'var(--red)', color: 'var(--red-lt)' }}
                    onClick={() => trocar({ tipo: 'encerrar' })}
                  >
                    Encerrar turma
                  </Botao>
                )}
              </div>
            )}
          </>
        )}

        {modo.tipo === 'editar' && (
          <EdicaoDaTurma
            turma={turma}
            onSalvar={(dados) =>
              void executar(
                () => turmasApi.alterar(turma.id, dados),
                (r) =>
                  [
                    'Turma alterada.',
                    r.aulas > 0 ? `${r.aulas} aulas da grade seguem a regra nova.` : null,
                    r.excecoesDescartadas > 0
                      ? `${r.excecoesDescartadas} aula(s) remarcada(s) à mão ficaram sem data na regra nova.`
                      : null,
                    r.promovidos.length > 0
                      ? `Da lista de espera: ${r.promovidos.map((p) => p.nome).join(', ')}.`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' '),
              )
            }
          />
        )}

        {(modo.tipo === 'suspender' || modo.tipo === 'encerrar') && (
          <MotivoDoCiclo
            acao={modo.tipo}
            onConfirmar={(motivo) =>
              void executar(
                () =>
                  modo.tipo === 'suspender'
                    ? turmasApi.suspender(turma.id, { motivo })
                    : turmasApi.encerrar(turma.id, { motivo }),
                (r) =>
                  modo.tipo === 'suspender'
                    ? `Turma suspensa. ${r.aulas} aulas saíram da grade.`
                    : `Turma encerrada. ${r.aulas} aulas saíram da grade e as matrículas foram encerradas.`,
              )
            }
          />
        )}

        {modo.tipo === 'sair' && (
          <SaidaDaTurma
            hoje={turma.hoje}
            matricula={modo.matricula}
            onConfirmar={(dados) =>
              void executar(
                () => turmasApi.sair(modo.matricula.matriculaId, dados),
                (r) =>
                  r.promovido
                    ? `${modo.matricula.aluno.nome} saiu da turma. ${r.promovido.nome}, primeiro da fila, ganhou a vaga.`
                    : `${modo.matricula.aluno.nome} saiu da turma. A vaga está livre.`,
              )
            }
          />
        )}

        {modo.tipo === 'transferir' && (
          <Transferencia
            turma={turma}
            matricula={modo.matricula}
            onConfirmar={(turmaDestinoId, data) =>
              void executar(
                () => turmasApi.transferir(modo.matricula.matriculaId, { turmaDestinoId, data }),
                (r) =>
                  `${modo.matricula.aluno.nome} transferido(a).${r.promovido ? ` ${r.promovido.nome} ganhou a vaga deixada.` : ''}`,
              )
            }
          />
        )}

        {erro && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erro}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}

// ---------------------------------------------------------------------
// Alunos e fila
// ---------------------------------------------------------------------

function ListaDeAlunos({
  turma,
  podeEditar,
  onSair,
  onTransferir,
}: {
  turma: DetalheDaTurma;
  podeEditar: boolean;
  onSair: (m: AlunoMatriculado) => void;
  onTransferir: (m: AlunoMatriculado) => void;
}) {
  const vagas = Math.max(0, turma.capacidade - turma.matriculados);
  return (
    <div>
      <div className="card-head" style={{ marginBottom: 10 }}>
        <h2>Alunos da turma</h2>
        <span className="hint">
          {vagas === 0
            ? 'lotada · lista de espera aberta'
            : `${vagas} ${vagas === 1 ? 'vaga livre' : 'vagas livres'}`}
        </span>
      </div>
      {turma.alunos.length === 0 ? (
        <Vazio>Ninguém matriculado ainda.</Vazio>
      ) : (
        <ul className="feed">
          {turma.alunos.map((a) => (
            <li key={a.matriculaId}>
              <Avatar nome={a.aluno.nome} />
              <div>
                <b>{a.aluno.nome}</b>
                <small>
                  {a.faltasSeguidas >= 2
                    ? `${a.faltasSeguidas} faltas seguidas`
                    : a.presenca === null
                      ? `matrícula em ${dataCompleta(a.entrouEm)}`
                      : `presença de ${a.presenca}% · ${a.aulas} aula${a.aulas === 1 ? '' : 's'}`}
                </small>
              </div>
              <div className="rt" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {a.faltasSeguidas >= 3 && <Status situacao="atrasada">Risco alto</Status>}
                {podeEditar && (
                  <>
                    <Botao variante="fantasma" pequeno onClick={() => onTransferir(a)}>
                      Transferir
                    </Botao>
                    <Botao variante="fantasma" pequeno onClick={() => onSair(a)}>
                      Tirar
                    </Botao>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ListaDeEspera({
  turma,
  podeEditar,
  onFeito,
}: {
  turma: DetalheDaTurma;
  podeEditar: boolean;
  onFeito: () => void;
}) {
  const avisar = useToast();
  const tirar = useMutation({ mutationFn: (id: string) => turmasApi.sairDaEspera(id) });

  if (turma.listaDeEspera.length === 0) return null;
  return (
    <div>
      <div className="card-head" style={{ marginBottom: 10 }}>
        <h2>Lista de espera</h2>
        <span className="hint">a vaga que abrir vai para o primeiro</span>
      </div>
      <ul className="feed">
        {turma.listaDeEspera.map((e) => (
          <li key={e.id}>
            <span className="sq" style={{ background: 'var(--panel-3)' }}>
              {e.posicao}º
            </span>
            <div>
              <b>{e.aluno.nome}</b>
              <small>esperando {haDias(e.diasEsperando)}</small>
            </div>
            {podeEditar && (
              <div className="rt">
                <Botao
                  variante="fantasma"
                  pequeno
                  disabled={tirar.isPending}
                  onClick={() =>
                    void tirar
                      .mutateAsync(e.id)
                      .then(() => {
                        onFeito();
                        avisar(`${e.aluno.nome} saiu da lista de espera.`);
                      })
                      .catch((erro: unknown) => avisar(mensagemDoErro(erro), 'aviso'))
                  }
                >
                  Tirar da fila
                </Botao>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Matricular por busca. Com a turma cheia — ou com gente esperando, que não
 * é furada —, a pessoa entra na lista de espera, e o toast diz a posição.
 */
function Matricular({ turma, onFeito }: { turma: DetalheDaTurma; onFeito: () => void }) {
  const avisar = useToast();
  const [busca, setBusca] = useState('');
  const termo = useAtrasado(busca.trim(), 300);
  const matriculados = new Set(turma.alunos.map((a) => a.aluno.id));
  const naFila = new Set(turma.listaDeEspera.map((e) => e.aluno.id));

  const alunos = useQuery({
    queryKey: ['alunos', 'listar', 'matricula', termo],
    queryFn: () => alunosApi.listar({ busca: termo, limite: 6 }),
    enabled: termo.length >= 2,
    retry: false,
  });
  const matricular = useMutation({
    mutationFn: (alunoId: string) => turmasApi.matricular(turma.id, { alunoId }),
  });

  async function escolher(alunoId: string, nome: string) {
    try {
      const resultado = await matricular.mutateAsync(alunoId);
      onFeito();
      setBusca('');
      avisar(
        resultado.situacao === 'Matriculado'
          ? `${nome} matriculado(a) na turma.`
          : `Turma lotada: ${nome} entrou na lista de espera, na posição ${resultado.posicao}.`,
        resultado.situacao === 'Matriculado' ? 'sucesso' : 'aviso',
      );
    } catch (e) {
      avisar(mensagemDoErro(e), 'aviso');
    }
  }

  const candidatos = (alunos.data?.itens ?? []).filter(
    (a) =>
      !matriculados.has(a.id) && !naFila.has(a.id) && !a.anonimizado && a.status !== 'Cancelado',
  );

  return (
    <div className="field">
      <label htmlFor="matricular-aluno">Matricular aluno</label>
      <input
        id="matricular-aluno"
        placeholder="Nome, telefone ou CPF"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
      />
      {turma.matriculados >= turma.capacidade && (
        <div className="help">
          A turma está lotada: quem for escolhido entra na lista de espera.
        </div>
      )}
      {candidatos.length > 0 && (
        <ul className="feed">
          {candidatos.map((a) => (
            <li
              key={a.id}
              className="clickable"
              role="button"
              tabIndex={0}
              onClick={() => void escolher(a.id, a.nome)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  void escolher(a.id, a.nome);
                }
              }}
            >
              <Avatar nome={a.nome} />
              <div>
                <b>{a.nome}</b>
                {a.apelido && <small>"{a.apelido}"</small>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Formulários dos casos de uso
// ---------------------------------------------------------------------

/**
 * O primeiro dia FORA da turma: hoje, ou amanhã quando a pessoa ainda fez
 * a aula de hoje — o servidor recusa sair no dia de uma chamada dela, e
 * não aceita nada além de amanhã (a vaga e a fila andam agora).
 */
function QuandoSai({
  hoje,
  valor,
  onChange,
}: {
  hoje: string;
  valor: string;
  onChange: (data: string) => void;
}) {
  return (
    <Campo rotulo="Fora da turma a partir de" ajuda="Amanhã, se já fez a aula de hoje.">
      <Escolhas
        aria-label="Fora da turma a partir de"
        opcoes={[
          { valor: hoje, rotulo: 'Hoje' },
          { valor: adicionarDias(hoje, 1), rotulo: 'Amanhã' },
        ]}
        valor={valor}
        onChange={onChange}
      />
    </Campo>
  );
}

function SaidaDaTurma({
  hoje,
  matricula,
  onConfirmar,
}: {
  hoje: string;
  matricula: AlunoMatriculado;
  onConfirmar: (dados: {
    motivo: 'Desistencia' | 'Outro';
    observacao?: string;
    data: string;
  }) => void;
}) {
  const [motivo, setMotivo] = useState<'Desistencia' | 'Outro'>('Desistencia');
  const [observacao, setObservacao] = useState('');
  const [data, setData] = useState(hoje);
  const [erros, setErros] = useState<Record<string, string>>({});

  return (
    <>
      <p className="sub">
        <b>{matricula.aluno.nome}</b> sai da turma. A matrícula não é apagada: ela fica na aba
        Turmas da ficha, com a data e o motivo, e a vaga vai para o primeiro da lista de espera.
      </p>
      <QuandoSai hoje={hoje} valor={data} onChange={setData} />
      <Campo rotulo="Motivo" erro={erros['motivo']}>
        <Escolhas
          aria-label="Motivo da saída"
          opcoes={MOTIVOS_DE_SAIDA_PEDIDA.map((m) => ({
            valor: m,
            rotulo: ROTULO_MOTIVO_DE_SAIDA[m],
          }))}
          valor={motivo}
          onChange={setMotivo}
        />
      </Campo>
      <Campo rotulo="Observação (opcional)" erro={erros['observacao']}>
        <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
      </Campo>
      <Botao
        style={{ alignSelf: 'flex-start' }}
        onClick={() => {
          const pedido = zSaidaDaTurma.safeParse({
            motivo,
            data,
            ...(observacao.trim() ? { observacao } : {}),
          });
          if (!pedido.success) {
            setErros(errosPorCampo(pedido.error.issues));
            return;
          }
          onConfirmar({
            motivo: pedido.data.motivo,
            data,
            ...(pedido.data.observacao ? { observacao: pedido.data.observacao } : {}),
          });
        }}
      >
        Confirmar saída
      </Botao>
    </>
  );
}

function Transferencia({
  turma,
  matricula,
  onConfirmar,
}: {
  turma: DetalheDaTurma;
  matricula: AlunoMatriculado;
  onConfirmar: (turmaDestinoId: string, data: string) => void;
}) {
  const lista = useQuery({ queryKey: ['turmas', 'lista'], queryFn: () => turmasApi.listar() });
  const destinos = (lista.data?.turmas ?? []).filter(
    (t) => t.id !== turma.id && t.status === 'Ativa',
  );
  const [destino, setDestino] = useState('');
  const [data, setData] = useState(turma.hoje);

  if (lista.isPending) return <EsqueletoDeCard linhas={2} />;
  return (
    <>
      <p className="sub">
        Transferir é sair desta turma e entrar na outra <b>na mesma operação</b>: as duas linhas
        ficam na ficha de {matricula.aluno.nome}, e a frequência de cada período continua certa.
      </p>
      {destinos.length === 0 ? (
        <Vazio>Não há outra turma ativa para receber a transferência.</Vazio>
      ) : (
        <Campo rotulo="Turma de destino">
          <select value={destino} onChange={(e) => setDestino(e.target.value)}>
            <option value="">Escolha a turma</option>
            {destinos.map((t) => (
              <option key={t.id} value={t.id} disabled={t.matriculados >= t.capacidade}>
                {t.titulo} · {gradeResumida(t.grade)} · {t.matriculados}/{t.capacidade}
                {t.matriculados >= t.capacidade ? ' · lotada' : ''}
              </option>
            ))}
          </select>
        </Campo>
      )}
      <QuandoSai hoje={turma.hoje} valor={data} onChange={setData} />
      <Botao
        style={{ alignSelf: 'flex-start' }}
        disabled={!destino}
        onClick={() => onConfirmar(destino, data)}
      >
        Confirmar transferência
      </Botao>
    </>
  );
}

function MotivoDoCiclo({
  acao,
  onConfirmar,
}: {
  acao: 'suspender' | 'encerrar';
  onConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  return (
    <>
      <p className="sub">
        {acao === 'suspender'
          ? 'Suspender é pausa: as aulas de hoje em diante saem da grade e a quadra fica livre; as matrículas e a lista de espera continuam. Reativar volta com o mesmo horário.'
          : 'Encerrar é o fim: a série acaba, as matrículas saem com o motivo "turma encerrada" e a lista de espera fecha. As chamadas feitas continuam na frequência.'}
      </p>
      <Campo rotulo="Motivo" erro={erro ?? undefined}>
        <textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </Campo>
      <Botao
        style={{ alignSelf: 'flex-start' }}
        onClick={() => {
          const pedido = zMotivoDaTurma.safeParse({ motivo });
          if (!pedido.success) {
            setErro(pedido.error.issues[0]?.message ?? 'Diga o motivo.');
            return;
          }
          onConfirmar(pedido.data.motivo);
        }}
      >
        {acao === 'suspender' ? 'Confirmar suspensão' : 'Confirmar encerramento'}
      </Botao>
    </>
  );
}

const NOMES_DOS_DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function EdicaoDaTurma({
  turma,
  onSalvar,
}: {
  turma: DetalheDaTurma;
  onSalvar: (dados: AlteracaoDeTurma) => void;
}) {
  const grade = useQuery({
    queryKey: ['agenda', 'grade', 'hoje'],
    queryFn: () => agendaApi.grade(),
  });
  const professores = useQuery({
    queryKey: ['turmas', 'professores'],
    queryFn: () => turmasApi.professores(),
  });

  const atual = turma.grade;
  const [niveis, setNiveis] = useState<NivelTurma[]>([...turma.niveis]);
  const [professorId, setProfessorId] = useState(turma.professor.id);
  const [capacidade, setCapacidade] = useState(String(turma.capacidade));
  const [quadraId, setQuadraId] = useState(atual?.quadra.id ?? '');
  const [dias, setDias] = useState<number[]>([...(atual?.diasSemana ?? [])]);
  const [horaInicio, setHoraInicio] = useState(atual?.horaInicio ?? '');
  const [duracao, setDuracao] = useState(atual?.duracaoMinutos ?? 60);
  const [aPartirDe, setAPartirDe] = useState(adicionarDias(turma.hoje, 1));
  const [erros, setErros] = useState<Record<string, string>>({});

  if (grade.isPending || professores.isPending) return <EsqueletoDeCard linhas={4} />;
  const janela = grade.data ? janelaDaGrade(grade.data.horarioFuncionamento) : null;
  // Até a meia-noite, como na criação — ver `ModalDeTurma`.
  const inicios = janela
    ? opcoesDeInicio(janela.abertura, Math.min(janela.fechamento - 30, MINUTOS_DO_DIA))
    : [horaInicio];
  const suspensa = turma.status === 'Suspensa';

  function salvar() {
    setErros({});
    const dados: Record<string, unknown> = {};
    if (niveis.join() !== turma.niveis.join()) dados['niveis'] = niveis;
    if (professorId !== turma.professor.id) dados['professorId'] = professorId;
    if (Number(capacidade) !== turma.capacidade) dados['capacidade'] = Number(capacidade);
    if (!suspensa && atual) {
      if (quadraId !== atual.quadra.id) dados['quadraId'] = quadraId;
      if (dias.join() !== atual.diasSemana.join()) dados['diasSemana'] = dias;
      if (horaInicio !== atual.horaInicio) dados['horaInicio'] = horaInicio;
      if (duracao !== atual.duracaoMinutos) dados['duracaoMinutos'] = duracao;
    }
    if (Object.keys(dados).length === 0) {
      setErros({ _: 'Nada mudou.' });
      return;
    }
    dados['aPartirDe'] = aPartirDe;
    const pedido = zAlteracaoDeTurma.safeParse(dados);
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }
    onSalvar(pedido.data);
  }

  return (
    <>
      {suspensa && (
        <Nota>
          A turma está suspensa: a grade volta com o último horário na reativação, e muda depois.
        </Nota>
      )}
      <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
        <Campo rotulo="Nível" erro={erros['niveis']}>
          <Escolhas
            aria-label="Nível"
            opcoes={NIVEL_TURMA.map((n) => ({ valor: n, rotulo: ROTULO_NIVEL_TURMA[n] }))}
            valor={niveis}
            onChange={(n) =>
              setNiveis((atuais) =>
                atuais.includes(n) ? atuais.filter((x) => x !== n) : [...atuais, n],
              )
            }
          />
        </Campo>
        <Campo rotulo="Professor" erro={erros['professorId']}>
          <select value={professorId} onChange={(e) => setProfessorId(e.target.value)}>
            {(professores.data ?? [])
              .filter((p) => p.ativo || p.id === turma.professor.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
          </select>
        </Campo>
        <Campo
          rotulo="Capacidade"
          erro={erros['capacidade']}
          ajuda="Aumentar anda a lista de espera na hora."
        >
          <input
            type="number"
            min={1}
            max={40}
            value={capacidade}
            onChange={(e) => setCapacidade(e.target.value)}
          />
        </Campo>
        {!suspensa && grade.data && (
          <>
            <Campo rotulo="Quadra" erro={erros['quadraId']}>
              <select value={quadraId} onChange={(e) => setQuadraId(e.target.value)}>
                {grade.data.quadras.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.nome}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo rotulo="Dias" erro={erros['diasSemana']}>
              <Escolhas
                aria-label="Dias da semana"
                opcoes={NOMES_DOS_DIAS.map((nome, dia) => ({ valor: String(dia), rotulo: nome }))}
                valor={dias.map(String)}
                onChange={(d) =>
                  setDias((atuais) =>
                    atuais.includes(Number(d))
                      ? atuais.filter((x) => x !== Number(d))
                      : [...atuais, Number(d)].sort((a, b) => a - b),
                  )
                }
              />
            </Campo>
            <Campo rotulo="Horário" erro={erros['horaInicio'] ?? erros['duracaoMinutos']}>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)}>
                  {[...new Set([...inicios, horaInicio])].sort().map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Duração"
                  value={duracao}
                  onChange={(e) => setDuracao(Number(e.target.value))}
                >
                  {[...new Set([60, 90, 120, duracao])]
                    .sort((a, b) => a - b)
                    .map((m) => (
                      <option key={m} value={m}>
                        {rotuloDeDuracao(m)}
                      </option>
                    ))}
                </select>
              </div>
            </Campo>
          </>
        )}
        <Campo
          rotulo="A mudança de grade vale a partir de"
          erro={erros['aPartirDe']}
          ajuda="Amanhã, no mínimo: a aula de hoje fica como está."
        >
          <input type="date" value={aPartirDe} onChange={(e) => setAPartirDe(e.target.value)} />
        </Campo>
      </Formulario>
      {(erros['_'] || primeiraMensagemDe(erros)) && (
        <div className="help" style={{ color: 'var(--red-lt)' }} role="alert">
          {erros['_'] ?? primeiraMensagemDe(erros)}
        </div>
      )}
      <Botao style={{ alignSelf: 'flex-start' }} onClick={salvar}>
        Salvar alterações
      </Botao>
    </>
  );
}

function primeiraMensagemDe(erros: Record<string, string>): string | undefined {
  return Object.entries(erros).find(([campo]) => campo !== '_')?.[1];
}
